/**
 * One action layer for every Inbox card kind. Hub-backed cards (`/api/cards/:id/act`) and private
 * asks (`p:` ids, answered through the ask flow) are the only ids the hub owns. Cards derived in
 * the browser from encrypted reports (`b:` build, `l:` landing, `a:` device/usage alert) exist
 * nowhere on the hub, so Done/Dismiss persist as a long snooze under the same opaque id: it is the
 * account's synced store, so the card stays hidden on every device and the hub learns nothing
 * about content. Semantics:
 *   Done    = resolved: the work behind the card is finished (hub cards only).
 *   Dismiss = hide without resolving; derived cards come back only for a new failure or alert.
 *   Snooze  = hide until a time.
 * A derived card also resolves itself when its condition clears (see `deriveCardItems`).
 */
import { ApiError } from './api.js'
import type { InboxItem } from './types.js'

export type CardOrigin = 'hub' | 'private-ask' | 'derived'
export type CardAction = 'done' | 'dismiss' | 'approve'

/** A dismissed derived card stays hidden this long (hub snoozes expire and are pruned). */
export const DISMISS_MS = 30 * 86_400_000

const DERIVED_ID = /^[abl]:/

export function cardOrigin(
  item: Pick<InboxItem, 'id' | 'itemId' | 'derived' | 'private'>,
): CardOrigin {
  if (item.derived || DERIVED_ID.test(item.id)) return 'derived'
  if (item.private || (item.itemId ?? item.id).startsWith('p:')) return 'private-ask'
  return 'hub'
}

export interface InboxActionApi {
  cardAct(
    id: string,
    action: 'approve' | 'dismiss' | 'done',
    extra?: Record<string, unknown>,
  ): Promise<unknown>
  snooze(id: string, untilMs: number): Promise<unknown>
}

export type ActionOutcome = 'done' | 'gone'

/** Hub ids come from the card; a derived id is never sent to a hub card endpoint. */
export async function actOnCard(
  api: InboxActionApi,
  item: InboxItem,
  action: CardAction,
  extra: Record<string, unknown> = {},
  now: number = Date.now(),
): Promise<ActionOutcome> {
  const origin = cardOrigin(item)
  if (origin === 'derived') {
    if (action === 'approve') throw new Error('This card has nothing to approve.')
    await api.snooze(item.id, now + DISMISS_MS)
    return 'done'
  }
  if (origin === 'private-ask')
    throw new Error('Answer this request in its session; it cannot be dismissed here.')
  const cardId = item.cardId ?? item.id.replace(/^c:/, '')
  try {
    await api.cardAct(cardId, action, extra)
    return 'done'
  } catch (e) {
    // Already resolved (another device, or auto-cleared): the outcome the user wanted.
    if (e instanceof ApiError && e.status === 404) return 'gone'
    throw e
  }
}

/** Snooze any card kind (the hub stores it by opaque id). */
export async function snoozeCard(api: InboxActionApi, item: InboxItem, until: number) {
  await api.snooze(item.itemId ?? item.id, until)
}

/** Where "Open" goes; undefined when a card has no detail to show. */
export type OpenTarget =
  | { to: '/s/$runId'; params: { runId: string } }
  | { to: '/inbox/$runId'; params: { runId: string } }
  | { to: '/w/$workstreamId'; params: { workstreamId: string } }
  | { to: '/releases' }
  | { to: '/settings/machines' }
  | { to: '/settings/notifications' }

export function openTarget(item: InboxItem): OpenTarget | undefined {
  if (item.category === 'device-alert')
    // machine-resources: a finished long job opens its run
    return item.runId
      ? { to: '/s/$runId', params: { runId: item.runId } }
      : { to: '/settings/machines' }
  // ---- g13-digests: a digest opens You › Notifications › Digests ----
  if (item.category === 'digest') return { to: '/settings/notifications' }
  // ---- end g13-digests ----
  if (item.type === 'build') return { to: '/releases' }
  if (item.derived && item.category === 'landing-failed') {
    if (item.runId) return { to: '/s/$runId', params: { runId: item.runId } }
    if (item.workstreamId)
      return { to: '/w/$workstreamId', params: { workstreamId: item.workstreamId } }
    return undefined
  }
  if (item.runId) return { to: '/inbox/$runId', params: { runId: item.runId } }
  return undefined
}

const CODE_TEXT: Record<string, string> = {
  not_found: 'That card is already gone.',
  too_many_snoozes: 'Too many hidden cards. Wake some from the Snoozed list first.',
  bad_item: 'This card cannot be changed.',
  locked: 'Unlock your account to do that.',
  unauthorized: 'Sign in again to do that.',
  forbidden: 'You do not have access to do that.',
}

/** Readable text for a failed card action; never a raw hub code. */
export function actionErrorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : ''
  if (CODE_TEXT[code]) return CODE_TEXT[code]
  const msg = (e as Error)?.message ?? ''
  if (CODE_TEXT[msg]) return CODE_TEXT[msg]
  if (!msg || /^[a-z0-9_:.-]+$/i.test(msg)) return 'Something went wrong. Try again.'
  return msg
}
